import { buildSelectedExtra } from '../utils';
import { CHART_MAP, ChartType } from './constants';

// ==================== 节点规则 ====================

// 摘要 / 参考文献 / 致谢 / 目录这类固定章不配图表
export const FIXED_CHAPTERS = ['摘要', 'ABSTRACT', '参考文献', '致谢', '目录'];

/** 摘要 / 参考文献这类固定章（不参与正文章节编号，也不配图表） */
export const isFixedChapter = (chapter) =>
    FIXED_CHAPTERS.some((fixed) =>
        (chapter?.chapter || '').trim().toUpperCase().includes(fixed.toUpperCase()),
    );

/** 没有节的正文章（开题报告 / 文献综述的模块）：图表选在章上，写作时后端按章读 selected_extra */
export const isLeafContentChapter = (chapter) =>
    !(chapter?.sections?.length > 0) && !isFixedChapter(chapter);

// ==================== 编号展示 ====================

// 标题自带编号：「第一章 …」「1.1 …」「2.3.1 …」「一、…」「1、…」
const OWN_NUMBERING =
    /^\s*(?:第\s*[一二三四五六七八九十百零〇\d]+\s*[章节部分篇]|(?:\d+[.．、]?)+(?=[\s　]|$)|[一二三四五六七八九十]+[、．.])/u;

/** 标题里已经写了章节号时不再另外显示序号徽标，避免「第2章 第一章 绪论」这种重复且对不上的情况 */
export const hasOwnNumbering = (title) => OWN_NUMBERING.test(title || '');

/**
 * 正文章的序号：摘要 / 参考文献等固定章不占号（返回 null），其余按出现顺序 1、2、3…
 * 小节编号用 `${章号}.${节序}`，这样「1.1」对应的就是正文第一章。
 */
export const chapterNumbers = (chapters) => {
    let n = 0;
    return (Array.isArray(chapters) ? chapters : []).map((chapter) =>
        isFixedChapter(chapter) ? null : ++n,
    );
};

// ==================== 图表统计 ====================

const selectedTypes = (value) => [
    ...new Set(
        (Array.isArray(value) ? value : String(value || '').split(','))
            .map((type) => String(type).trim())
            .filter(Boolean),
    ),
];

/** 只统计最末级节点的实际选择；父级说明不重复计数 */
export function countOutlineResources(nodes) {
    const result = { figures: 0, tables: 0 };
    for (const node of Array.isArray(nodes) ? nodes : []) {
        if (!node || typeof node !== 'object') continue;
        const children = [
            ...(Array.isArray(node.sections) ? node.sections : []),
            ...(Array.isArray(node.subsections) ? node.subsections : []),
        ];
        if (children.length) {
            const childCount = countOutlineResources(children);
            result.figures += childCount.figures;
            result.tables += childCount.tables;
            continue;
        }
        const extras = Array.isArray(node.extras)
            ? node.extras
            : selectedTypes(node.selected_extra).map(
                  (type) => ({ DIAGRAM: 'image', TABLE: 'table' })[type],
              );
        if (extras.includes('image')) {
            result.figures += selectedTypes(node.selectedChartType).length;
        }
        if (extras.includes('table')) result.tables += 1;
    }
    return result;
}

// ==================== 编辑：插入内容 / 图表类型 ====================

/** 「切换插入内容」的单节点变换（同步 selected_extra） */
export const makeExtraToggle = (sectionId, type) => (item) => {
    if (item.id !== sectionId) return item;
    const extras = item.extras || [];
    const nextExtras = extras.includes(type)
        ? extras.filter((extra) => extra !== type)
        : [...extras, type];
    return { ...item, extras: nextExtras, selected_extra: buildSelectedExtra(nextExtras) };
};

/**
 * 在三级大纲树中应用单节点变换。
 * parentId 为空 = 章级（没有节的叶子章，如开题报告 / 文献综述的模块）。
 */
export const applyToOutlineNode = (prev, transform, parentId, grandParentId) =>
    prev.map((ch) => {
        if (!parentId && !grandParentId) return transform(ch);
        if (grandParentId) {
            if (ch.id !== grandParentId) return ch;
            return {
                ...ch,
                sections: (ch.sections || []).map((sec) =>
                    sec.id === parentId
                        ? { ...sec, subsections: (sec.subsections || []).map(transform) }
                        : sec,
                ),
            };
        }
        if (ch.id === parentId) {
            return { ...ch, sections: (ch.sections || []).map(transform) };
        }
        return ch;
    });

/** 「图表选择更新」的单节点变换（selectedChartType / chartTypeCn） */
export const makeChartSectionUpdate = (sectionId, chartUpdate) => (sec) => {
    if (sec.id !== sectionId) return sec;
    const curTypes = sec.selectedChartType
        ? typeof sec.selectedChartType === 'string'
            ? sec.selectedChartType.split(',').filter(Boolean)
            : sec.selectedChartType
        : [];
    const { action, chartType, chartTypeCn: newCtCn, customText } = chartUpdate;

    const withImage = (extras, hasImage) => {
        let next = [...(extras || [])];
        if (hasImage && !next.includes('image')) next.push('image');
        if (!hasImage) next = next.filter((extra) => extra !== 'image');
        return next;
    };

    if (action === 'toggle') {
        const isSelected = curTypes.includes(chartType);
        const nextTypes = isSelected
            ? curTypes.filter((type) => type !== chartType)
            : [...curTypes, chartType];
        const nextCtCn = { ...(sec.chartTypeCn || {}) };
        if (isSelected) {
            delete nextCtCn[chartType];
        } else {
            nextCtCn[chartType] = newCtCn?.[chartType] || CHART_MAP[chartType]?.label || chartType;
        }
        const nextExtras = withImage(sec.extras, nextTypes.length > 0);
        return {
            ...sec,
            selectedChartType: nextTypes.join(','),
            chartTypeCn: Object.keys(nextCtCn).length > 0 ? nextCtCn : null,
            extras: nextExtras,
            selected_extra: buildSelectedExtra(nextExtras),
        };
    }
    if (action === 'updateCustom') {
        const nextCtCn = { ...(sec.chartTypeCn || {}), [ChartType.OTHER]: customText };
        const nextTypes = curTypes.includes(ChartType.OTHER)
            ? curTypes
            : [...curTypes, ChartType.OTHER];
        const nextExtras = withImage(sec.extras, true);
        return {
            ...sec,
            selectedChartType: nextTypes.join(','),
            chartTypeCn: nextCtCn,
            extras: nextExtras,
            selected_extra: buildSelectedExtra(nextExtras),
        };
    }
    if (action === 'clearAll') {
        const nextExtras = withImage(sec.extras, false);
        return {
            ...sec,
            selectedChartType: '',
            chartTypeCn: null,
            extras: nextExtras,
            selected_extra: buildSelectedExtra(nextExtras),
        };
    }
    return sec;
};

/** 「联网搜索关键词」的单节点变换（extraTypeCn.DATA） */
export const makeDataKeywordUpdate = (sectionId, keyword) => (sec) => {
    if (sec.id !== sectionId) return sec;
    const text = (keyword || '').trim();
    const nextExtraTypeCn = { ...(sec.extraTypeCn || {}) };
    let nextExtras = [...(sec.extras || [])];
    const nextExtraDesc = { ...(sec.extraDesc || {}) };
    if (text) {
        nextExtraTypeCn.DATA = text;
        if (!nextExtras.includes('webSearch')) nextExtras.push('webSearch');
    } else {
        delete nextExtraTypeCn.DATA;
        delete nextExtraDesc.webSearch;
        nextExtras = nextExtras.filter((extra) => extra !== 'webSearch');
    }
    return {
        ...sec,
        extraTypeCn: Object.keys(nextExtraTypeCn).length > 0 ? nextExtraTypeCn : null,
        extras: nextExtras,
        extraDesc: nextExtraDesc,
        selected_extra: buildSelectedExtra(nextExtras),
    };
};

// ==================== 图表说明解析（仅展示用） ====================
// 特调引擎给出的说明通常写成「图名 | 类型：…；目的：…；元素与文字：…」，原始字符串始终原样保留。

const TITLE_SEPARATOR = /[|｜]/u;
const SEGMENT_SEPARATOR = /[；;\r\n]+/u;
const KEY_VALUE = /^\s*([^：:]{1,16}?)\s*[：:]\s*(.*)$/su;
const TITLE_PREFIX = /^(?:图名|图题|表名|表题|名称|标题)\s*[：:]\s*/u;
const TYPE_KEY = /^(?:图表?类型|类型|图型|表格类型|类别)$/u;

const clean = (value) => (typeof value === 'string' ? value.trim() : '');

const stripTitlePrefix = (value) =>
    clean(value)
        .replace(TITLE_PREFIX, '')
        .replace(/[。．.]+$/u, '')
        .trim();

const parseFields = (body) => {
    const fields = [];
    let lead = '';
    for (const segment of clean(body).split(SEGMENT_SEPARATOR)) {
        const text = segment.trim();
        if (!text) continue;
        const matched = text.match(KEY_VALUE);
        if (matched && matched[2].trim()) {
            fields.push({ key: matched[1].trim(), value: matched[2].trim() });
        } else if (fields.length > 0) {
            const last = fields[fields.length - 1];
            last.value = `${last.value}；${text}`;
        } else {
            lead = lead ? `${lead}；${text}` : text;
        }
    }
    return { fields, lead };
};

export function parsePlanDescription(description, fallbackTitle = '') {
    const raw = clean(description);
    const fallback = clean(fallbackTitle);
    if (!raw) return { title: fallback, type: '', fields: [], raw, structured: false };

    let titlePart = '';
    let body = raw;
    const pipeIndex = raw.search(TITLE_SEPARATOR);
    if (pipeIndex >= 0) {
        titlePart = raw.slice(0, pipeIndex);
        body = raw.slice(pipeIndex + 1);
    }

    const { fields: parsedFields, lead } = parseFields(body);
    let title = stripTitlePrefix(titlePart);
    let leadText = lead;
    if (!title && pipeIndex < 0 && leadText && parsedFields.length > 0) {
        title = stripTitlePrefix(leadText);
        leadText = '';
    }

    const fields = [];
    if (leadText) fields.push({ key: '说明', value: leadText });
    for (const field of parsedFields) {
        if (!title && TITLE_PREFIX.test(`${field.key}：`)) {
            title = stripTitlePrefix(field.value);
        } else {
            fields.push(field);
        }
    }

    const structured = fields.length >= 2 || (fields.length === 1 && !!title);
    if (!structured) {
        return { title: title || fallback, type: '', fields: [], raw, structured: false };
    }
    const typeIndex = fields.findIndex((field) => TYPE_KEY.test(field.key));
    return {
        title: title || fallback,
        type: typeIndex >= 0 ? fields[typeIndex].value : '',
        fields: fields.filter((_, index) => index !== typeIndex),
        raw,
        structured: true,
    };
}

/** 短到可以放进一行标签的说明不需要卡片展示 */
export const isCompactDescription = (description) => {
    const text = clean(description);
    return Array.from(text).length <= 40 && !/[|｜\r\n；;]/u.test(text);
};
